/**
 * Where the map sheet's parts sit on the shell's 320×200 frame, and what
 * colour each is inked in.
 *
 * The layout is the original shell's own full-bleed panel screen
 * (`shell/render/panel-chrome.ts`): the 17-row header band with its wing bars
 * and title, then the body. The body is split the way a printed map sheet is —
 * the map in a sunken well on the left, a marginal key board down the right —
 * rather than the way a modern map app is, because there is no pan, no zoom
 * and no cursor here: the sheet shows the whole region at once, which is what
 * the boxed paper maps did and what makes matching terrain out of the window
 * to ink on the sheet possible at all.
 *
 * **The ink.** The printed geography is drawn from `PS.PAL`'s 224–255 block,
 * the one `shell/render/ps-palette.ts` establishes is identical across every
 * screen palette in the game. 224–242 run light warm buff to near-black brown
 * and 243–254 are the burnt-orange ramp, which is exactly a sepia map: buff
 * paper, brown printed detail, orange for the two things that are *not* printed
 * geography (the front, and the pilot's own pencil).
 *
 * **Where sepia alone was not enough.** Drawing every road, river and field
 * boundary in one brown made a busy region unreadable — "it currently is just
 * chaotic lines" (user, 2026-08-23) — and the 224–255 block has no blue and no
 * green to separate them with. So the classes the data distinguishes
 * (`map-features.ts`) are separated by *value* where the tan ramp allows it
 * (roads darkest, field boundaries nearly paper) and by `map-raster.ts`'s
 * synthetic inks where it does not (water blue, woods green). Blue water and
 * green woods over buff is what a printed map of the period looked like, so
 * the sheet gains legibility without leaving its idiom.
 */

import { createMapProjection, type MapProjection } from "../assets/world-map-projection.ts";
import { insetRect, type Rect } from "../shell/render/draw-ops.ts";
import { MARKER_ORANGE_INDEX, WATER_FILL_INDEX, WATER_LINE_INDEX, WOOD_GREEN_INDEX } from "./map-raster.ts";
import type { MapFeature } from "./map-features.ts";
import type { MapBounds } from "./map-model.ts";

/** The sunken well the map is drawn in, on the shell's logical frame. */
export const MAP_WELL: Rect = { x: 3, y: 19, w: 196, h: 178 };
/** The raised board down the right margin: the key, the readouts and the legend. */
export const KEY_BOARD: Rect = { x: 202, y: 19, w: 115, h: 178 };

/** The paper itself — inside the well's two bevel rings (`shell/render/plate-chrome.ts`'s `sunkenWellOps`). */
export const MAP_PAPER: Rect = insetRect(MAP_WELL, 2);
/** The key board's usable inside, likewise. */
export const KEY_INSIDE: Rect = insetRect(KEY_BOARD, 3);

/** FONT3 is 8 rows tall; 9 gives the key board's rows one row of air, matching the shell's own list pitch of `SLOT_HEIGHT − 1`. */
export const KEY_ROW_PITCH = 9;

/** Paper, and the inks printed on it. */
export const PAPER = 225;
export const GRID_INK = 230;
export const SCENERY_INK = 233;
/** Printed detail the data does not let us name — see `FEATURE_INK`. AD-016 inked the whole network in this one brown, which is what made a busy region unreadable. */
export const NETWORK_INK = 236;

/**
 * Roads, in the darkest brown short of the pencil ink. A road is the mark a
 * pilot actually navigates by — it is straight, it runs between two towns, and
 * it is the one network class you can match to the ground from 6 000 ft — so it
 * gets the strongest contrast against buff paper of anything printed.
 */
export const ROAD_INK = 240;

/**
 * Field boundaries, four steps off the paper itself. They are the most
 * numerous class and the least useful one — a hedge line tells a pilot nothing —
 * so rather than drop them (the sheet has no zoom to drop them *below*, and the
 * texture they give the cultivated ground is worth keeping) they are printed
 * faint enough that the roads and rivers read straight through them. Kept off
 * `GRID_INK`'s own 230 so a pixel census can still tell the two apart.
 */
export const FIELD_INK = 229;

/** Rivers, canals and the coastline: the sheet's synthetic blue. Period maps inked water blue over buff, so this is in idiom as well as legible. */
export const WATER_INK = WATER_LINE_INDEX;
/** The sea and the estuaries, as a pale wash under everything else. */
export const SEA_INK = WATER_FILL_INDEX;
/** Woods, heath and the landcover plates' edges, in the sheet's synthetic green — the other colour a period map used over buff. */
export const WOOD_INK = WOOD_GREEN_INDEX;

/**
 * The whole ink scheme for the terrain layer, in one place, so the key board
 * and the painter cannot disagree about what a colour means.
 */
export const FEATURE_INK: Readonly<Record<MapFeature, number>> = {
  water: WATER_INK,
  road: ROAD_INK,
  field: FIELD_INK,
  wood: WOOD_INK,
  other: NETWORK_INK,
};
/** Near-black brown: everything printed in bold — aerodrome rings, hill contours, the grid's own lettering. */
export const PENCIL_INK = 241;
/** The burnt-orange ramp: the front line, heavy, because it is the one thing on the sheet that can kill you. */
export const FRONT_INK = 246;
/** A lighter orange for the briefed route and its target marks — pencilled on before take-off, not printed. */
export const ROUTE_INK = 250;
/**
 * The aeroplane's own mark: a vivid synthetic orange (`map-raster.ts`'s
 * `MARKER_ORANGE_INDEX` — `PS.PAL`'s own 243–254 ramp is burnt orange, too
 * close to the front line to carry the pilot, so the mark gets its own ink;
 * user, 2026-08-23). It has to be findable in one glance against a sheet that
 * is otherwise brown on buff, and it must not read as any of the printed
 * furniture — which is exactly what REAL NAVIGATION takes away.
 */
export const PLAYER_INK = MARKER_ORANGE_INDEX;

/**
 * TARGET crosses in `PS.PAL`'s own pure red (216) — the sheet's second
 * glance-findable ink, distinct from the player's orange and from the front
 * line's orange (user, 2026-08-23).
 */
export const TARGET_INK = 216;

/**
 * How much of the paper's short edge is left as margin. Small on purpose: the
 * sheet has one job and one scale, so there is nothing to gain by floating the
 * region in the middle of it. `/assets`' World viewer keeps its own 8 %, which
 * suits a pane you can drag around.
 */
const MAP_PADDING_FRACTION = 0.03;

/**
 * The region's map-space bounds fitted to the paper, in paper-local pixels
 * (`(0, 0)` is `MAP_PAPER`'s top-left corner). Uniform scale and Y-flipped, so
 * increasing `v` draws up the sheet — the same projection, from the same
 * module, that `/assets`' 2D World pane uses, which is what makes the two show
 * a region the same way up.
 */
export function mapProjection(bounds: MapBounds): MapProjection {
  return createMapProjection(
    { minX: bounds.minU, maxX: bounds.maxU, minY: bounds.minV, maxY: bounds.maxV },
    { width: MAP_PAPER.w, height: MAP_PAPER.h },
    { zoom: 1, panX: 0, panY: 0 },
    MAP_PADDING_FRACTION,
  );
}
